import React from 'react';
import { TextField, Button, CircularProgress, useTheme } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

interface QuizAnswerFormProps {
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
  onSubmit: (e: React.FormEvent) => void;
  placeholder: string;
  highlighted?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

interface FormInputs {
  answer: string;
}

export const QuizAnswerForm: React.FC<QuizAnswerFormProps> = ({
  value,
  onChange,
  disabled,
  onSubmit,
  placeholder,
  highlighted = false,
  inputRef,
}) => {
  const theme = useTheme();
  const { control } = useForm<FormInputs>({ defaultValues: { answer: value }, values: { answer: value } });

  return (
    <form onSubmit={onSubmit}>
      <Controller
        name="answer"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            onChange={(e) => {
              field.onChange(e);
              onChange(e.target.value);
            }}
            inputRef={inputRef}
            fullWidth
            variant="outlined"
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: highlighted ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'action.hover') : 'background.default',
                color: highlighted ? theme.palette.common.white : 'text.primary',
                fontSize: '1.25rem',
                fontWeight: 'medium',
                '& fieldset': {
                  borderWidth: 2,
                },
              },
            }}
          />
        )}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={disabled || !value.trim()}
        sx={{
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 'bold',
          borderRadius: 3,
          boxShadow: 3,
          textTransform: 'none',
          backgroundColor: highlighted ? theme.palette.common.white : 'primary.main',
          color: highlighted ? 'primary.main' : theme.palette.common.white,
          '&:hover': {
            backgroundColor: highlighted ? theme.palette.grey[100] : 'primary.dark',
          },
        }}
      >
        {disabled ? <CircularProgress size={26} color="inherit" /> : 'Submit Answer'}
      </Button>
    </form>
  );
};
